import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAuthToken, clearAuthToken } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      
      if (response.data.authenticated) {
        setUser({
          email: response.data.dropbox_email,
          name: response.data.dropbox_name,
          accountId: response.data.dropbox_account_id,
          isSettingsAdmin: !!response.data.is_settings_admin,
        });
        setIsAuthenticated(true);
        return true;
      } else {
        clearAuthToken();
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error?.message || String(error));
      clearAuthToken();
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Login function - stores the token and updates state
  const login = useCallback((token, userData, refreshToken = '') => {
    setAuthToken(token, refreshToken);
    setUser({
      ...(userData || {}),
      isSettingsAdmin: !!(userData?.isSettingsAdmin || userData?.is_settings_admin),
    });
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error?.message || String(error));
    } finally {
      // Always clear local state regardless of API response
      clearAuthToken();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    checkAuth,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
